import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  ButtonComponentActionResultTypenames,
  ButtonComponentV2,
  LendingPaymentSource,
  ProcessTypeInput,
} from '@varo/graphql-schema-types';
import {
  Href,
  useGlobalSearchParams,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useCancelTabapayTransfer from 'features/moveMoney/screens/dcf/hooks/useCancelTabapayTransfer';
import { useLogGQLErrorScreenAnalytics } from 'features/moveMoney/screens/dcf/hooks/useLogGQLErrorScreenAnalytics';
import { useThreeDsHandler } from 'features/moveMoney/screens/dcf/hooks/useThreeDsHandler';
import useThreeDsSdkWeb from 'features/moveMoney/screens/dcf/hooks/useThreeDsSdkWeb';
import {
  getFlowNameForAnalytics,
  TabaPayEntryFlowType,
} from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import { useTabaPayAddress } from 'features/moveMoney/screens/dcf/tabapay/providers/TabaPayContextProvider';
import useDeleteExternalCard from 'features/myVaro/screens/ExternalAccounts/hooks/useDeleteExternalCard';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { parseGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import { isWeb } from 'shared/constants/platform';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { getAddressText } from 'shared/utils/smartyStreet/smartyStreet';

import useExternalCardDetails from './useExternalCardDetails';

export const useExternalCardsDetails = () => {
  const router = useRouter();
  const { cardId } = useGlobalSearchParams<{
    cardId: string;
  }>();
  const {
    tabaPayEntryFlowType,
    dueDate,
    feeAmount,
    amountId,
    advanceAmount,
    repaymentDate,
    selectedAccountId,
    paymentSource,
    selectedAccountName,
  } = useLocalSearchParams<{
    tabaPayEntryFlowType?: TabaPayEntryFlowType;
    dueDate?: string;
    feeAmount?: string;
    amountId?: string;
    advanceAmount?: string;
    repaymentDate?: string;
    selectedAccountId?: string;
    paymentSource?: LendingPaymentSource;
    selectedAccountName?: string;
  }>();
  const { segmentClient } = useAnalyticsConfiguration();
  const { top } = useSafeAreaInsets();
  const [isVerifyCardScreenShown, setIsVerifyCardScreenShown] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>();
  const removeBottomSheetRef = useRef<BottomSheetModal>(null);
  const { externalCardDetails, fetching, errorModel, reExecuteQuery } =
    useExternalCardDetails();
  const {
    deleteExternalCard,
    isLoading,
    errorModel: deleteExternalCardError,
  } = useDeleteExternalCard({ shouldNavigateToExternalAccounts: true });

  const {
    updatedMessage,
    setUpdatedMessage,
    shouldRefreshTransactions,
    setVerificationScreen,
  } = useTabaPayAddress();

  const { handleThreeDS, errorScreenThreeDs, initTransactionId } =
    useThreeDsHandler();
  const {
    startThreeDsWebFlow,
    isReady,
    errorScreenThreeDs: webErrorScreen,
    initTransactionId: initTransactionIdWeb,
  } = useThreeDsSdkWeb();

  const shouldRefreshPage = useCallback(() => {
    reExecuteQuery({ requestPolicy: 'network-only' });
  }, [reExecuteQuery]);

  useEffect(() => {
    if (updatedMessage || shouldRefreshTransactions) {
      shouldRefreshPage();
    }
  }, [shouldRefreshPage, updatedMessage, shouldRefreshTransactions]);

  const linkedCardRemoveFailedEvent = usePressableAnalytics({
    analyticsId: 'LinkedCardRemoveFailed',
    customAttributes: {
      card_type: 'debit_card',
      flow_name: 'card_management',
      failure_reason: deleteExternalCardError?.messageHeadline,
    },
  });

  const formattedAddress = getAddressText(externalCardDetails?.address);
  const transactions = externalCardDetails?.transactions;
  const {
    cancelTabapayTransfer,
    isLoading: cancelTransferLoading,
    isSuccess,
    errorScreen: cancelTransferErrorScreen,
  } = useCancelTabapayTransfer();

  const flowName = getFlowNameForAnalytics(tabaPayEntryFlowType!);

  useEffect(() => {
    if (isSuccess) {
      navigateBack();
    }
  }, [isSuccess]);
  const combinedErrorScreen =
    errorModel ||
    deleteExternalCardError ||
    errorScreenThreeDs ||
    webErrorScreen ||
    cancelTransferErrorScreen;

  useEffect(() => {
    if (combinedErrorScreen) {
      setLoading(false);
    }
  }, [combinedErrorScreen]);

  useEffect(() => {
    if (deleteExternalCardError) {
      removeBottomSheetRef.current?.dismiss();
      linkedCardRemoveFailedEvent();
    }
  }, [deleteExternalCardError, linkedCardRemoveFailedEvent]);

  const verifyCard = async () => {
    if (!cardId) {
      return;
    }
    setIsVerifyCardScreenShown(false);
    setLoading(true);
    try {
      if (isWeb && isReady) {
        const response = await startThreeDsWebFlow(
          ProcessTypeInput.CardRegistration,
          flowName,
          cardId,
        );
        if (response) {
          shouldRefreshPage();
        }
      } else {
        const result = await handleThreeDS(
          ProcessTypeInput.CardRegistration,
          flowName,
          cardId,
        );
        if (result) {
          shouldRefreshPage();
        }
      }
    } catch (error) {
      logErrorWithContext(error, {
        featureArea: FeatureArea.EXTERNAL_CARD_DETAILS,
        responsibleTeam: ResponsibleTeam.PAYMENTS_TRANSFER,
        severity: ErrorSeverity.LOW,
      });
    } finally {
      setLoading(false);
    }
  };

  useLogGQLErrorScreenAnalytics(
    combinedErrorScreen?.analytics,
    'External Card Details',
    flowName,
  );

  const handleErrorButtonPress = (button?: ButtonComponentV2) => {
    const action = button?.buttonOnClick;
    switch (action?.__typename) {
      case ButtonComponentActionResultTypenames.RouteNavigationAction:
        router.replace(action.route as Href);
        if (action.analyticsEvent) {
          const { name, properties } = parseGQLAnalyticsEvent(
            action.analyticsEvent,
          );
          void segmentClient.logEvent(name, {
            ...properties,
            flow_name: flowName,
          });
        }
        break;

      case ButtonComponentActionResultTypenames.SDContinueClientFlowAction:
        if (action.analyticsEvent) {
          const { name, properties } = parseGQLAnalyticsEvent(
            action.analyticsEvent,
          );
          void segmentClient.logEvent(name, {
            ...properties,
            flow_name: flowName,
          });
        }
        if (deleteExternalCardError) {
          void deleteExternalCard({ externalCardId: cardId });
          removeBottomSheetRef.current?.present();
        } else if (errorScreenThreeDs || webErrorScreen) {
          void verifyCard();
        }
        break;

      case ButtonComponentActionResultTypenames.SDBackNavigationAction:
        if (action.analyticsEvent) {
          const { name, properties } = parseGQLAnalyticsEvent(
            action.analyticsEvent,
          );
          void segmentClient.logEvent(name, {
            ...properties,
            flow_name: flowName,
          });
        }
        if (button?.id === 'EXIT_BUTTON') {
          if (
            webErrorScreen?.processType === ProcessTypeInput.Transaction ||
            errorScreenThreeDs?.processType === ProcessTypeInput.Transaction
          ) {
            void cancelTabapayTransfer(
              {
                transactionId:
                  initTransactionId?.trim() || initTransactionIdWeb,
              },
              flowName,
            );
          } else {
            navigateBack();
          }
        } else {
          navigateBack();
        }
        break;
    }
  };

  useEffect(() => {
    if (externalCardDetails?.verifyCardActionScreen) {
      setVerificationScreen(externalCardDetails.verifyCardActionScreen);
    }
  }, [externalCardDetails?.verifyCardActionScreen, setVerificationScreen]);

  const shouldShowLoader = fetching || loading!;
  const shouldShowBackButton =
    !(isWeb && combinedErrorScreen && !combinedErrorScreen?.is3DSError) &&
    !shouldShowLoader &&
    !isVerifyCardScreenShown;

  return {
    shouldShowBackButton,
    tabaPayEntryFlowType,
    dueDate,
    feeAmount,
    amountId,
    advanceAmount,
    repaymentDate,
    selectedAccountId,
    paymentSource,
    selectedAccountName,
    formattedAddress,
    top,
    transactions,
    cancelTransferLoading,
    isLoading,
    updatedMessage,
    isVerifyCardScreenShown,
    externalCardDetails,
    shouldShowLoader,
    combinedErrorScreen,
    initTransactionId,
    initTransactionIdWeb,
    removeBottomSheetRef,
    cardId,
    deleteExternalCard,
    handleErrorButtonPress,
    setUpdatedMessage,
    setIsVerifyCardScreenShown,
    verifyCard,
    cancelTabapayTransfer,
    errorScreenThreeDs,
    webErrorScreen,
  };
};
